/**
 * DecisionFlow — i18n bootstrap
 * M041 Phase 1 · S221 · May 2026
 * M041 Phase 2 · S223 · May 2026
 *
 * Usage:
 *   import { initI18n } from './i18n';
 *   await initI18n();                          // Phase 1 — en only, no overrides (boot)
 *   await initI18n('en-US', overrides);       // Phase 2 — with tenant overrides (unused path)
 *
 *   import { applyTenantOverrides } from './i18n';
 *   applyTenantOverrides('en-US', overrides); // Phase 2 — inject after login, i18n already init
 *
 * Fallback chain (Phase 2+):
 *   ui_message_override (tenant) → en.json → hardcoded key name
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import common    from './locales/en/common.json';
import errors    from './locales/en/errors.json';
import auth      from './locales/en/auth.json';
import nav       from './locales/en/nav.json';
import admin     from './locales/en/admin.json';
import configure from './locales/en/configure.json';
import load      from './locales/en/load.json';
import dataSetup from './locales/en/dataSetup.json';
import execute   from './locales/en/execute.json';
import finance   from './locales/en/finance.json';
import manager   from './locales/en/manager.json';
import rep       from './locales/en/rep.json';
import platform from './locales/en/platform.json';


const EN_RESOURCES = {
  common, errors, auth, nav, admin,
  configure, load, dataSetup, execute,
  finance, manager, rep, platform,
};

/**
 * initI18n — call once at app boot before rendering.
 * Initialises i18next with English base strings only.
 * Do NOT call again after boot — use applyTenantOverrides() instead.
 *
 * @param {string} locale    - BCP-47 locale code e.g. 'en-US'. Defaults to 'en'.
 * @param {object} overrides - Per-namespace override maps (unused in Phase 1 boot path).
 */
export async function initI18n(locale = 'en', overrides = {}) {
  const baseLocale = locale.split('-')[0]; // 'en-US' → 'en'

  const resources = {
    [baseLocale]: Object.fromEntries(
      Object.entries(EN_RESOURCES).map(([ns, base]) => [
        ns,
        { ...base, ...(overrides[ns] ?? {}) },
      ])
    ),
  };

  await i18n.use(initReactI18next).init({
    lng: baseLocale,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: Object.keys(EN_RESOURCES),
    resources,
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  return i18n;
}

/**
 * applyTenantOverrides — call after login, once tenant-context and tenant-strings
 * have been fetched. Merges per-tenant string overrides into the already-initialised
 * i18next instance using addResourceBundle (deep merge, overwrite).
 *
 * Must NOT be called before initI18n() has resolved.
 *
 * @param {string} locale    - BCP-47 locale code from tenant-context e.g. 'en-US'.
 * @param {object} overrides - Per-namespace override maps built from tenant-strings response.
 *                             Shape: { common: { key: value }, admin: { key: value }, ... }
 */
export function applyTenantOverrides(locale, overrides = {}) {
  const baseLocale = locale.split('-')[0]; // 'en-US' → 'en'

  // Change active language if it differs from boot default.
  if (i18n.language !== baseLocale) {
    i18n.changeLanguage(baseLocale);
  }

  // Merge each namespace's overrides into the live bundle.
  // deep=true, overwrite=true — tenant values win over en.json base.
  Object.entries(overrides).forEach(([ns, keys]) => {
    i18n.addResourceBundle(baseLocale, ns, keys, true, true);
  });
}

export default i18n;
